/** Driving instrument UI. Visual contract: docs/DESIGN_SYSTEM.md. */
import { DESIGN_SPEED_KMH } from '../core/spec';
import type { Quality } from '../core/perf';
import type { CameraMode } from '../car/chaseCamera';

export interface Hud {
  setSpeed(kmh: number): void;
  setStreet(name: string): void;
  setCameraMode(mode: CameraMode): void;
  setQuality(quality: Quality): void;
  setOffRoad(offRoad: boolean): void;
  setAssessmentStatus(label: string | null): void;
  setDiagnostics(lines: string[]): void;
  toggleDiagnostics(): void;
  readonly diagnosticsVisible: boolean;
  dispose(): void;
}

const QUALITY_LABEL: Record<Quality, string> = {
  high: '高畫質',
  medium: '平衡',
  low: '省電',
};

const CAMERA_LABEL: Record<CameraMode, string> = {
  first: '駕駛席',
  chase: '追蹤',
};

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

export function createHud(root: HTMLElement, initialQuality: Quality): Hud {
  const wrap = node('section', 'hud');
  wrap.setAttribute('aria-label', '駕駛資訊');

  const primary = node('div', 'hud-panel hud-primary');
  const speedWrap = node('div', 'hud-speed-wrap');
  speedWrap.appendChild(node('span', 'hud-kicker', '車速'));
  const speedEl = node('div', 'hud-speed');
  // A plain span is intentional. <output> has an implicit live status role in
  // accessibility APIs, which would announce a value that changes every frame.
  const speedValue = node('span', '', '0');
  speedValue.setAttribute('aria-label', '時速');
  const speedUnit = node('small', '', 'km/h');
  speedEl.append(speedValue, speedUnit);
  speedWrap.appendChild(speedEl);
  const limit = node('span', 'hud-limit', String(DESIGN_SPEED_KMH));
  limit.setAttribute('aria-label', `設計速度限制 ${DESIGN_SPEED_KMH} 公里每小時`);
  const meter = node('div', 'hud-meter');
  meter.setAttribute('aria-hidden', 'true');
  const meterFill = document.createElement('i');
  meter.appendChild(meterFill);
  primary.append(speedWrap, limit, meter);

  const context = node('div', 'hud-context');
  const street = node('div', 'hud-street');
  street.appendChild(node('span', 'hud-kicker', '目前道路'));
  const streetValue = node('strong', '', '—');
  street.appendChild(streetValue);

  const status = node('div', 'hud-status');
  const cameraChip = node('span', 'hud-chip', CAMERA_LABEL.first);
  cameraChip.setAttribute('aria-label', '視角');
  const qualityChip = node('span', 'hud-chip', QUALITY_LABEL[initialQuality]);
  qualityChip.setAttribute('aria-label', '畫質');
  const diagButton = node('button', 'hud-diag-toggle', '效能');
  diagButton.type = 'button';
  diagButton.title = '顯示效能診斷（F）';
  diagButton.setAttribute('aria-keyshortcuts', 'F');
  const assessmentChip = node('span', 'hud-chip hud-assessment', 'Experimental driving');
  assessmentChip.hidden = true;
  assessmentChip.setAttribute('aria-label', '訓練評核狀態');
  status.append(cameraChip, qualityChip, assessmentChip, diagButton);
  context.append(street, status);

  const diagEl = node('pre', 'hud-diag');
  diagEl.id = 'performance-diagnostics';
  let diagVisible = new URLSearchParams(location.search).get('diag') === '1';
  diagEl.hidden = !diagVisible;
  diagButton.setAttribute('aria-controls', diagEl.id);
  diagButton.setAttribute('aria-expanded', String(diagVisible));

  const alert = node('div', 'hud-alert', '已離開行車路面 · 正在協助返回道路');
  alert.hidden = true;
  alert.setAttribute('role', 'status');

  // Event-driven live copy. Speed is deliberately excluded: announcing it at
  // frame rate would make the simulator unusable with a screen reader.
  const live = node('span', 'hud-live');
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('aria-atomic', 'true');

  wrap.append(primary, context, diagEl, live);
  root.append(wrap, alert);

  const setDiagVisible = (visible: boolean) => {
    diagVisible = visible;
    diagEl.hidden = !visible;
    diagButton.setAttribute('aria-expanded', String(visible));
  };
  const toggleDiagnostics = () => setDiagVisible(!diagVisible);
  const onKey = (event: KeyboardEvent) => {
    if (event.code === 'KeyF' && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      toggleDiagnostics();
    }
  };
  const onDiagClick = () => toggleDiagnostics();
  window.addEventListener('keydown', onKey);
  diagButton.addEventListener('click', onDiagClick);

  let shownSpeed = -1;
  let currentStreet = '';
  let currentCamera: CameraMode = 'first';
  let currentQuality = initialQuality;
  let currentlyOffRoad = false;

  return {
    setSpeed(kmh) {
      const value = Math.max(0, Math.round(kmh));
      if (value === shownSpeed) return;
      shownSpeed = value;
      speedValue.textContent = String(value);
      primary.classList.toggle('is-over', value > DESIGN_SPEED_KMH);
      meterFill.style.width = `${Math.min(100, (value / DESIGN_SPEED_KMH) * 100)}%`;
    },
    setStreet(name) {
      if (!name || currentStreet === name) return;
      currentStreet = name;
      streetValue.textContent = name;
      live.textContent = `目前道路：${name}`;
    },
    setCameraMode(mode) {
      if (mode === currentCamera) return;
      currentCamera = mode;
      cameraChip.textContent = CAMERA_LABEL[mode];
      live.textContent = `已切換至${CAMERA_LABEL[mode]}視角`;
    },
    setQuality(quality) {
      if (quality === currentQuality) return;
      currentQuality = quality;
      qualityChip.textContent = QUALITY_LABEL[quality];
      live.textContent = `畫質已調整為${QUALITY_LABEL[quality]}`;
    },
    setOffRoad(offRoad) {
      if (offRoad === currentlyOffRoad) return;
      currentlyOffRoad = offRoad;
      alert.hidden = !offRoad;
      live.textContent = offRoad ? alert.textContent : '已返回行車路面';
    },
    setAssessmentStatus(label) {
      assessmentChip.hidden = !label;
      if (label) assessmentChip.textContent = label;
    },
    setDiagnostics(lines) {
      if (diagVisible) diagEl.textContent = lines.join('\n');
    },
    toggleDiagnostics,
    get diagnosticsVisible() {
      return diagVisible;
    },
    dispose() {
      window.removeEventListener('keydown', onKey);
      diagButton.removeEventListener('click', onDiagClick);
      wrap.remove();
      alert.remove();
    },
  };
}
